"use client";
import { getApiBaseUrl } from "@/lib/apiBase";
import { fetchJsonArray } from "@/lib/fetchJsonSafe";

import { useState, useRef, useEffect } from "react";
import { useSession } from "next-auth/react";
import { ChevronLeft, ChevronRight, ArrowRight, UserPlus, Users } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
const BASE_URL = getApiBaseUrl();

function CustomerCard({ customer, rank, reviewCount, session, followCount, isFollowing, onFollowToggle }) {
  const [imageError, setImageError] = useState(false);
  const isPublic = customer.public === true;
  const isOwnProfile = session?.id === customer.id;

  return (
    <Link href={`/customer/${customer.id}`}>
      <div className="group relative flex h-full cursor-pointer flex-col overflow-hidden rounded-3xl border border-slate-200/80 bg-white p-0 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-slate-200/70">
        {/* Review Count Badge - only show how many reviews, no star icon */}
        <div className="absolute top-3 right-3 z-20 flex items-center rounded-full border border-slate-200/60 bg-white/95 px-3 py-1.5 text-xs font-semibold text-slate-800 shadow-lg backdrop-blur-md">
          <span>{reviewCount} сэтгэгдэл</span>
        </div>

        {/* Customer Avatar */}
        <div className="relative h-56 w-full overflow-hidden bg-gray-100">
          <div className="pointer-events-none absolute inset-x-0 top-0 z-10 h-20 bg-gradient-to-b from-black/25 to-transparent" />
          {/* Хувийн (public=false) профайл бол зураг харуулахгүй, зөвхөн инициал */}
          {(!isPublic && !isOwnProfile) ? (
            <div className="w-full h-full flex flex-col items-center justify-center bg-gray-100">
              <div className="text-7xl font-extrabold text-slate-400 mb-2">
                {customer.name?.[0]?.toUpperCase() || '?'}
              </div>
              <div className="text-xs text-slate-500 font-medium px-4 text-center">
                Хувийн профайл
              </div>
            </div>
          ) : customer.image && !imageError ? (
            <>
              <Image
                src={`${BASE_URL}${customer.image}`}
                alt={customer.name || "Хэрэглэгч"}
                fill
                className="object-cover transition-transform duration-500 ease-out group-hover:scale-105"
                onError={() => setImageError(true)}
              />
            </>
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center bg-gray-100">
              <div className="text-7xl font-extrabold text-slate-400 mb-2">
                {customer.name?.[0]?.toUpperCase() || '?'}
              </div>
              <div className="text-xs text-slate-500 font-medium px-4 text-center">
                {customer.name || 'Хэрэглэгч'}
              </div>
            </div>
          )}
        </div>

        {/* Content Section - Below Image */}
        <div className="flex flex-1 flex-col p-5">
          {/* Customer Name - First after image */}
          <h3 className="mb-2 line-clamp-2 text-base font-bold text-slate-900">
            {customer.name || "Хэрэглэгч"}
          </h3>

          {/* Privacy info only */}
          <div className="mb-4 text-sm text-slate-600">
            <span className="inline-flex rounded-full bg-slate-100 px-2.5 py-1 text-xs text-slate-600">
              Профайлын тохиргоо: {isPublic ? "Нийтийн" : "Хувийн"}
            </span>
          </div>

          {/* Follow Section */}
          <div className="mt-auto space-y-2 border-t border-slate-100 pt-3">
            {/* Follow Button - Only show if logged in as customer and not own profile */}
            {session?.id && (session?.type === 'customer' || session?.user?.type === 'customer') && session.id !== customer.id && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  onFollowToggle(customer.id);
                }}
                className={`w-full flex items-center justify-center gap-1.5 rounded-xl px-3 py-2 text-xs font-medium transition-colors ${
                  isFollowing
                    ? "bg-[#577099] text-white hover:bg-[#4a5f85]"
                    : "border border-slate-200 bg-slate-100 text-slate-700 hover:bg-slate-200"
                }`}
              >
                <UserPlus className={`w-3.5 h-3.5 ${isFollowing ? 'text-white' : 'text-slate-600'}`} />
                <span className="font-medium">
                  {isFollowing ? 'Following' : 'Follow'}
                </span>
              </button>
            )}
            {/* Follow Count - Always show if count > 0 */}
            {followCount > 0 && (
              <div className="flex items-center justify-center gap-1 rounded-lg bg-slate-50 py-1.5 text-xs text-slate-600">
                <Users className="w-3 h-3" />
                <span className="font-medium">{followCount} followers</span>
              </div>
            )}
          </div>

          {/* Contact Info - Phone and Email (only if public or own profile) */}
          {(isPublic || isOwnProfile) && (
            <div className="pt-3 border-t border-slate-100 space-y-1.5">
              {customer.phone && (
                <div className="flex items-center gap-1.5 text-xs text-slate-600 truncate">
                  <svg className="w-3.5 h-3.5 flex-shrink-0 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  <span className="truncate">{customer.phone}</span>
                </div>
              )}
              {customer.email && (
                <div className="flex items-center gap-1.5 text-xs text-slate-600 truncate">
                  <svg className="w-3.5 h-3.5 flex-shrink-0 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <span className="truncate">{customer.email}</span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </Link>
  );
}

export default function TopCustomers() {
  const { data: session } = useSession();
  const [currentPage, setCurrentPage] = useState(0);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [followStatus, setFollowStatus] = useState({}); // { customerId: { isFollowing: boolean, followCount: number } }
  const containerRef = useRef(null);

  useEffect(() => {
    const fetchTopCustomers = async () => {
      try {
        setLoading(true);
        
        // Fetch all reviews to group by customer
        const reviewsData = await fetchJsonArray(`/api/review?type=review`);
        
        // Fetch all customers
        const customersData = await fetchJsonArray(`/api/customer?type=customer`);
        
        // Group reviews by customer (reviewer_id) – зөвхөн сэтгэгдлийн тоо
        const customerReviewMap = {};
        reviewsData.forEach((review) => {
          const customerId = review.reviewer_id;
          if (!customerReviewMap[customerId]) {
            customerReviewMap[customerId] = {
              reviewCount: 0,
            };
          }
          customerReviewMap[customerId].reviewCount += 1;
        });
        
        // Combine customer data with review counts (дунджаар үнэлгээ ашиглахгүй)
        const customersWithStats = customersData
          .map((customer) => {
            const reviewData = customerReviewMap[customer.id] || { reviewCount: 0 };
            const reviewCount = reviewData.reviewCount;
            
            return {
              ...customer,
              reviewCount,
            };
          })
          .sort((a, b) => {
            // Зөвхөн сэтгэгдлийн тоогоор эрэмбэлнэ (ихээс бага руу)
            if (b.reviewCount !== a.reviewCount) {
              return b.reviewCount - a.reviewCount;
            }
            // Хэрвээ ижил байвал нэрээр нь эрэмбэлнэ
            return (a.name || "").localeCompare(b.name || "");
          });
        
        setCustomers(customersWithStats);

        const ids = customersWithStats.map((customer) => customer.id).filter(Boolean);
        const followerId =
          session?.id && (session?.type === "customer" || session?.user?.type === "customer")
            ? session.id
            : "0";

        if (ids.length > 0) {
          try {
            const summaryRes = await fetch(
              `/api/customer/follow?type=summary&follower_id=${encodeURIComponent(
                followerId
              )}&ids=${encodeURIComponent(ids.join(","))}`
            );
            const summaryData = await summaryRes.json();
            const items = summaryData?.items || {};
            const followStatusMap = {};
            ids.forEach((id) => {
              const item = items[id] || {};
              followStatusMap[id] = {
                isFollowing: !!item.isFollowing,
                followCount: Number(item.followCount || 0),
              };
            });
            setFollowStatus(followStatusMap);
          } catch (error) {
            console.error("Error fetching follow summary:", error);
            const emptyFollowMap = {};
            ids.forEach((id) => {
              emptyFollowMap[id] = { isFollowing: false, followCount: 0 };
            });
            setFollowStatus(emptyFollowMap);
          }
        } else {
          setFollowStatus({});
        }
      } catch (error) {
        console.error("Error fetching top customers:", error);
        setCustomers([]);
      } finally {
        setLoading(false);
      }
    };

    fetchTopCustomers();
  }, [session]);

  const customersPerPage = 4; // 4 columns x 1 row
  const totalPages = Math.ceil(customers.length / customersPerPage);
  const displayedCustomers = customers.slice(
    currentPage * customersPerPage,
    (currentPage + 1) * customersPerPage
  );
  
  if (loading) {
    return (
      <div className="max-w-[1440px] mx-auto py-8 px-4 mt-5">
        <div className="text-center py-12">
          <div className="text-slate-500">Ачааллаж байна...</div>
        </div>
      </div>
    );
  }

  const goToPrev = () => {
    setCurrentPage((prev) => Math.max(0, prev - 1));
  };

  const goToNext = () => {
    setCurrentPage((prev) => Math.min(totalPages - 1, prev + 1));
  };

  const handleFollowToggle = async (customerId) => {
    if (!session?.id || session?.type !== 'customer') {
      alert('Please login to follow');
      return;
    }

    if (session.id === customerId) {
      return; // Don't allow following yourself
    }

    const currentStatus = followStatus[customerId] || { isFollowing: false, followCount: 0 };

    // Optimistic update
    setFollowStatus((prev) => ({
      ...prev,
      [customerId]: {
        isFollowing: !currentStatus.isFollowing,
        followCount: currentStatus.isFollowing
          ? Math.max(0, currentStatus.followCount - 1)
          : currentStatus.followCount + 1,
      },
    }));

    try {
      const response = await fetch('/api/customer/follow', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          follower_id: session.id,
          following_id: customerId,
        }),
      });

      if (!response.ok) {
        // Revert on error
        setFollowStatus((prev) => ({
          ...prev,
          [customerId]: currentStatus,
        }));
        alert('Алдаа гарлаа. Дахин оролдоно уу.');
      } else {
        const data = await response.json();
        setFollowStatus((prev) => ({
          ...prev,
          [customerId]: {
            isFollowing: data.isFollowing || false,
            followCount: data.followCount || 0,
          },
        }));
      }
    } catch (error) {
      console.error('Error toggling follow:', error);
      // Revert on error
      setFollowStatus((prev) => ({
        ...prev,
        [customerId]: currentStatus,
      }));
      alert('Алдаа гарлаа. Дахин оролдоно уу.');
    }
  };

  return (
    <div className="max-w-[1440px] mx-auto py-12 px-4">
      {/* Header Section */}
      <div className="mb-10">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="h-1 w-8 bg-[#577099] rounded-full"></div>
              <span className="text-xs font-medium text-blue-600 uppercase tracking-wide">Онцлох хэрэглэгчид</span>
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900">
              Топ Хэрэглэгчид
            </h1>
            <p className="mt-1 text-slate-600 text-sm">
              Хамгийн их сэтгэгдэл бичсэн хэрэглэгчид
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Link
              href="/customers"
              className="flex items-center gap-2 px-6 py-3 bg-[#577099] text-white rounded-full hover:bg-[#4a5f85] transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 font-semibold"
            >
              <span className="text-sm">Бүгдийг харах</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
            <div className="flex items-center gap-2 bg-white rounded-full p-1 shadow-md border border-slate-200">
              <button
                onClick={goToPrev}
                disabled={currentPage === 0}
                className={`flex h-9 w-9 items-center justify-center rounded-full ${
                  currentPage === 0
                    ? "opacity-30 cursor-not-allowed text-slate-400"
                    : "text-slate-700"
                }`}
                aria-label="Previous page"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <span className="px-3 text-sm font-semibold text-slate-600">
                {currentPage + 1} / {totalPages}
              </span>
              <button
                onClick={goToNext}
                disabled={currentPage === totalPages - 1}
                className={`flex h-9 w-9 items-center justify-center rounded-full ${
                  currentPage === totalPages - 1
                    ? "opacity-30 cursor-not-allowed text-slate-400"
                    : "text-slate-700"
                }`}
                aria-label="Next page"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Customers Grid - 4 columns */}
      <div
        ref={containerRef}
        className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6 items-stretch"
      >
        {displayedCustomers.map((customer, index) => {
          const customerFollowStatus = followStatus[customer.id] || { isFollowing: false, followCount: 0 };
          return (
            <CustomerCard
              key={customer.id}
              customer={customer}
              reviewCount={customer.reviewCount}
              session={session}
              followCount={customerFollowStatus.followCount}
              isFollowing={customerFollowStatus.isFollowing}
              onFollowToggle={handleFollowToggle}
            />
          );
        })}
      </div>

      {/* Empty State */}
      {displayedCustomers.length === 0 && !loading && (
        <div className="text-center py-16">
          <div className="text-slate-400 text-lg">Хэрэглэгч олдсонгүй</div>
        </div>
      )}
    </div>
  );
}

